/**
 * §5 確定履歴から取込 → 参照列 の frontend データモデル (Decision #2/#4,
 * s07-confirm-history-import-reference-view.md)。
 *
 * 参照列 state (confirmRecordRefs) は過去実績 (pastActualDateStrs) と並列に
 * 持つ独立した state で、PreviewConfirmRecord 応答をそのまま保持する。
 * KomaGrid / ForecastChart への表示用 (label, slots) への解決は、対象の
 * scope (RETAIL 追加時の retailerBusinessCd 固定 / BG_TOTAL 追加時の
 * per-retailer bgResults) と現在の表示切替 (targetScope /
 * selectedRetailerBusinessCd) を突き合わせて都度行う (resolveConfirmRecordRefColumn)。
 *
 * BG_TOTAL scope で追加した参照列は、retailer scope へ切替えた時に対応する
 * retailer の値を bgResults から抽出して表示する (追加 fetch 不要)。BG_TOTAL
 * ビューで表示する列は、bgResults の per-retailer 値を合算した BG 合計として
 * 表示する (RETAIL 追加時の参照列は追加時の retailer 以外のビューでは
 * 表示しない — その retailer 固有の確定値のため)。
 */
import type {
  RetailerPreviewEntry,
  SlotValue,
} from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'

import {
  calcBgTotalSlots,
  protoSlotsToKoma,
} from '~/pages/DemandForecastDaily/models/slotHelpers'
import type {
  KomaSlots,
  TargetScope,
} from '~/pages/DemandForecastDaily/models/types'

/** 参照列 5 件上限 (Decision #3: 過去実績・類似日ダイアログと揃える)。 */
export const MAX_CONFIRM_RECORD_REFS = 5

/**
 * 参照列 1 件が凡例 hiddenKeys / ForecastChart 系列で使う key の prefix。
 * `confirmRef_<confirm_record_id>` 形 (ForecastChart.tsx / chartLegend.ts が同形で
 * 系列 key・凡例 key を生成する)。参照列の削除/クリア/リセット時に対応する
 * hiddenKey を捨てる連動 (useConfirmRecordImportFlow) の識別に使う。
 */
export const CONFIRM_REF_LEGEND_KEY_PREFIX = 'confirmRef_'

/** confirm_record_id → 凡例 hiddenKeys / ForecastChart 系列 key (confirmRef_<id>)。 */
export function confirmRefLegendKey(id: string): string {
  return `${CONFIRM_REF_LEGEND_KEY_PREFIX}${id}`
}

/**
 * PreviewConfirmRecord 応答から構築する参照列 1 件分。id は confirm_record_id
 * (UUID、chip / ヘッダの × クリックで除去する対象)。
 */
export type ConfirmRecordRef =
  | {
      scope: 'RETAIL'
      id: string
      label: string
      retailerBusinessCd: string
      forecastSlots: SlotValue[]
    }
  | {
      scope: 'BG_TOTAL'
      id: string
      label: string
      bgResults: RetailerPreviewEntry[]
    }

/** KomaGrid / ForecastChart へ渡す解決済み列 (extraColumns 形)。 */
export type ResolvedConfirmRecordRefColumn = {
  id: string
  label: string
  slots: KomaSlots
}

/**
 * ref を現在の表示 (targetScope / selectedRetailerBusinessCd) に照らして
 * 解決する。表示対象外 (RETAIL 追加時の別 retailer 表示中、または BG_TOTAL
 * 追加時に選択中の retailer が bgResults に含まれない = AC-DFD-032
 * missing_retailer_business_cds に該当) の場合は undefined を返す。
 */
export function resolveConfirmRecordRefColumn(
  ref: ConfirmRecordRef,
  targetScope: TargetScope,
  selectedRetailerBusinessCd: string | undefined,
): ResolvedConfirmRecordRefColumn | undefined {
  if (targetScope === 'UNATTRIBUTED') return undefined
  if (ref.scope === 'RETAIL') {
    if (
      targetScope !== 'RETAILER' ||
      selectedRetailerBusinessCd !== ref.retailerBusinessCd
    )
      return undefined
    return {
      id: ref.id,
      label: ref.label,
      slots: protoSlotsToKoma(ref.forecastSlots),
    }
  }

  if (targetScope === 'BG_TOTAL') {
    return {
      id: ref.id,
      label: ref.label,
      slots: calcBgTotalSlots(
        ref.bgResults.map((r) => protoSlotsToKoma(r.forecastSlots)),
      ),
    }
  }
  if (selectedRetailerBusinessCd === undefined) return undefined
  const match = ref.bgResults.find(
    (r) => r.retailerBusinessCd === selectedRetailerBusinessCd,
  )
  if (match === undefined) return undefined
  return {
    id: ref.id,
    label: ref.label,
    slots: protoSlotsToKoma(match.forecastSlots),
  }
}

/** 現在の表示に対して解決可能な参照列だけを順序維持で返す。 */
export function resolveConfirmRecordRefColumns(
  refs: ConfirmRecordRef[],
  targetScope: TargetScope,
  selectedRetailerBusinessCd: string | undefined,
): ResolvedConfirmRecordRefColumn[] {
  const out: ResolvedConfirmRecordRefColumn[] = []
  for (const ref of refs) {
    const col = resolveConfirmRecordRefColumn(
      ref,
      targetScope,
      selectedRetailerBusinessCd,
    )
    if (col !== undefined) out.push(col)
  }
  return out
}

/**
 * 確定履歴の列ラベル (PreviewConfirmRecordResponse.label = backend の
 * 「確定 MM/DD」、確定日時 JST の月日) から、画面の列ヘッダー (過去実績列) と
 * 同じ「M/D」(ゼロ埋めなし) の月日を取り出す (AC-DFD-048 の通知文 {MM/dd}、
 * issue #7436)。通知文の「確定」の語は各 locale の文言側に持たせ、backend の
 * 日本語ラベルを英語画面に入れないためのもの。形式が合わない時は undefined。
 */
export function confirmedMonthDayFromLabel(label: string): string | undefined {
  const match = /(\d{1,2})\/(\d{1,2})\s*$/.exec(label)
  if (match === null) return undefined
  return `${String(Number(match[1]))}/${String(Number(match[2]))}`
}
